import {
  resolveFeatureFlag,
  type FeatureFlagKey,
  type FeatureFlagValue,
} from '@todesktop/shared';
import { IGlobalState, selectedApp, store, useStore } from '../../store';

// User overrides apply to the signed-in user, not the owner of the selected
// app. Server-side checks for an app use its owner.
const resolveFromState = <K extends FeatureFlagKey>(
  key: K,
  state: IGlobalState,
): FeatureFlagValue<K> =>
  resolveFeatureFlag(key, {
    app: selectedApp(state) ?? null,
    global: state.globalFeatureFlags,
    user: state.user,
  });

/** Resolves a flag for the signed-in user and the selected app */
export const useFeatureFlag = <K extends FeatureFlagKey>(
  key: K,
): FeatureFlagValue<K> => useStore((state) => resolveFromState(key, state));

/** Non-React version of `useFeatureFlag` */
export const getFeatureFlag = <K extends FeatureFlagKey>(
  key: K,
): FeatureFlagValue<K> => resolveFromState(key, store.getRawState());

/**
 * Whether flags resolve to their real values yet: the global values and the
 * signed-in user have loaded. Until then every flag reads its default.
 */
export const useFeatureFlagsLoaded = (): boolean =>
  useStore(
    (state) => state.globalFeatureFlags !== null && state.userDataLoaded,
  );
